import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Stack } from '@mui/material';
import { PlusCircle, Users, FileText, Star, ClipboardList, Megaphone, Gift, DollarSign, Ticket, Send } from 'lucide-react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import StatCard from '../components/dashboard/StatCard';
import ReviewTrendsChart from '../components/dashboard/ReviewTrendsChart';
import ReviewsByMonthChart from '../components/dashboard/ReviewsByMonthChart';
import SentimentAnalysisChart from '../components/dashboard/SentimentAnalysisChart';
import RewardRequestItem from '../components/dashboard/RewardRequestItem';
import QuickActionButton from '../components/dashboard/QuickActionButton';
import { mockQuery } from '../data/dashboardMockData';
import { formatPercentageChange, formatRating, formatReviewCount } from '../utils/dashboardFormatters';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getCampaigns } from '../services/campaignService';
import { getReviews, getMonthlyReviews } from '../services/reviewsService';
import { getRewardClaims, approveRewardClaim, rejectRewardClaim } from '../services/rewardsService';
import { getCampaignParticipants } from '../services/contactService';
import type { RewardRequest } from '../types/dashboard';

const Dashboard = () => {
  const navigate = useNavigate();
  // Profile is fetched by ProtectedRoute wrapper
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  
  // Dashboard metrics state
  const [metrics, setMetrics] = useState({
    totalCampaigns: 0,
    totalCustomers: 0,
    totalReviewsSent: 0,
    reviewsPosted: 0,
    rewardsClaimed: 0
  });
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  
  // Reward requests state
  const [rewardRequests, setRewardRequests] = useState<RewardRequest[]>([]);
  const [isLoadingRewards, setIsLoadingRewards] = useState(true);
  
  // Reviews by month state
  const [reviewsByMonth, setReviewsByMonth] = useState<{ month: string; count: number }[]>([]);
  const [isLoadingReviewsByMonth, setIsLoadingReviewsByMonth] = useState(true);
  const [hasMonthlyReviews, setHasMonthlyReviews] = useState(false);

  // Fetch dashboard metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setIsLoadingMetrics(true);
        
        // Fetch all data in parallel
        const [campaignsResponse, reviewsResponse, rewardsResponse] = await Promise.all([
          getCampaigns(100, 0),
          getReviews(100, 0),
          getRewardClaims()
        ]);

        // Calculate total campaigns
        const totalCampaigns = campaignsResponse.success && campaignsResponse.data?.campaigns
          ? campaignsResponse.data.campaigns.length
          : 0;

        // Calculate total customers and total reviews sent
        // Total reviews sent = sum of participants across all campaigns
        let totalCustomers = 0;
        let totalReviewsSent = 0;

        if (campaignsResponse.success && campaignsResponse.data?.campaigns) {
          // Fetch participants for each campaign
          const participantPromises = campaignsResponse.data.campaigns.map(async (campaign) => {
            try {
              const participantsResponse = await getCampaignParticipants(campaign.id, 100, 0);
              if (participantsResponse.success && participantsResponse.data?.contacts) {
                return participantsResponse.data.contacts.length;
              }
              return 0;
            } catch (error) {
              console.error(`Error fetching participants for campaign ${campaign.id}:`, error);
              return 0;
            }
          });

          const participantCounts = await Promise.all(participantPromises);
          totalCustomers = participantCounts.reduce((sum, count) => sum + count, 0);
          totalReviewsSent = totalCustomers; // Total reviews sent = total customers (one review per customer)
        }

        // Calculate reviews posted
        const reviewsPosted = reviewsResponse.success && reviewsResponse.data?.reviews
          ? reviewsResponse.data.reviews.filter(review => review.review_posted).length
          : 0;

        // Calculate rewards claimed
        const rewardsClaimed = rewardsResponse.success && rewardsResponse.data?.claims
          ? rewardsResponse.data.claims.length
          : 0;

        setMetrics({
          totalCampaigns,
          totalCustomers,
          totalReviewsSent,
          reviewsPosted,
          rewardsClaimed
        });
      } catch (error) {
        console.error('Error fetching dashboard metrics:', error);
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, []);

  // Fetch reward requests
  useEffect(() => {
    const fetchRewardRequests = async () => {
      try {
        setIsLoadingRewards(true);
        const response = await getRewardClaims();
        
        if (response.success && response.data?.claims) {
          // Filter for pending claims and take top 5
          const pendingClaims = response.data.claims
            .filter(claim => claim.status === 'pending' || claim.status === 'not_claimed')
            .slice(0, 5)
            .map(claim => ({
              id: claim.id,
              userName: claim.customer_name,
              rewardType: claim.reward_description || `${claim.reward_type} - ${claim.reward_value}`,
              avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
              status: 'pending' as const
            }));
          
          setRewardRequests(pendingClaims);
        } else {
          setRewardRequests([]);
        }
      } catch (error) {
        console.error('Error fetching reward requests:', error);
        setRewardRequests([]);
      } finally {
        setIsLoadingRewards(false);
      }
    };

    fetchRewardRequests();
  }, []);

  // Fetch monthly reviews
  useEffect(() => {
    const fetchMonthlyReviews = async () => {
      try {
        setIsLoadingReviewsByMonth(true);
        const response = await getMonthlyReviews();
        
        if (response.success && response.data?.months) {
          const months = response.data.months;
          
          // Transform API data to chart format
          // Extract short month name from "December 2025" -> "Dec"
          const transformedData = months.map(item => {
            // Extract month name from "December 2025" format
            const monthName = item.month_name.split(' ')[0]; // Gets "December"
            // Convert to short format (first 3 letters)
            const shortMonth = monthName.substring(0, 3);
            
            return {
              month: shortMonth,
              count: item.total_reviews
            };
          });
          
          // Check if there are any reviews (total > 0)
          const hasReviews = response.data.total > 0;
          setHasMonthlyReviews(hasReviews);
          
          if (hasReviews) {
            setReviewsByMonth(transformedData);
          } else {
            // Use dummy data when no reviews
            setReviewsByMonth(mockQuery.reviewsByMonth);
          }
        } else {
          // No data or error - use dummy data
          setHasMonthlyReviews(false);
          setReviewsByMonth(mockQuery.reviewsByMonth);
        }
      } catch (error) {
        console.error('Error fetching monthly reviews:', error);
        // On error, use dummy data
        setHasMonthlyReviews(false);
        setReviewsByMonth(mockQuery.reviewsByMonth);
      } finally {
        setIsLoadingReviewsByMonth(false);
      }
    };

    fetchMonthlyReviews();
  }, []);

  const handleApproveReward = async (id: string) => {
    try {
      const response = await approveRewardClaim(id);
      if (response.success) {
        // Remove the approved reward from the list
        setRewardRequests(prev => prev.filter(req => req.id !== id));
        // Refresh the list
        const refreshResponse = await getRewardClaims();
        if (refreshResponse.success && refreshResponse.data?.claims) {
          const pendingClaims = refreshResponse.data.claims
            .filter(claim => claim.status === 'pending' || claim.status === 'not_claimed')
            .slice(0, 5)
            .map(claim => ({
              id: claim.id,
              userName: claim.customer_name,
              rewardType: claim.reward_description || `${claim.reward_type} - ${claim.reward_value}`,
              avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
              status: 'pending' as const
            }));
          setRewardRequests(pendingClaims);
        }
      }
    } catch (error) {
      console.error('Error approving reward:', error);
    }
  };

  const handleRejectReward = async (id: string) => {
    try {
      const response = await rejectRewardClaim(id);
      if (response.success) {
        // Remove the rejected reward from the list
        setRewardRequests(prev => prev.filter(req => req.id !== id));
        // Refresh the list
        const refreshResponse = await getRewardClaims();
        if (refreshResponse.success && refreshResponse.data?.claims) {
          const pendingClaims = refreshResponse.data.claims
            .filter(claim => claim.status === 'pending' || claim.status === 'not_claimed')
            .slice(0, 5)
            .map(claim => ({
              id: claim.id,
              userName: claim.customer_name,
              rewardType: claim.reward_description || `${claim.reward_type} - ${claim.reward_value}`,
              avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
              status: 'pending' as const
            }));
          setRewardRequests(pendingClaims);
        }
      }
    } catch (error) {
      console.error('Error rejecting reward:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      <Sidebar user={userData} />
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <Box sx={{ p: 4 }}>
          {/* Page Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: '#111827',
                mb: 0.5
              }}
            >
              Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#6B7280',
                fontSize: '0.875rem',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Showing data for the Last 30 Days
            </Typography>
          </Box>

          {/* Stats Cards - Single Row with 5 Metrics */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)', 
                lg: 'repeat(5, 1fr)' 
              },
              gap: 3,
              mb: 3
            }}
          >
            <StatCard
              title="Total Campaigns"
              value={isLoadingMetrics ? '...' : metrics.totalCampaigns.toLocaleString()}
              change=""
              timePeriod=""
              icon={<Megaphone />}
              backgroundColor="#F3E8FF"
              iconGradient="linear-gradient(140.67deg, #FB9C40 15.47%, #FB7B40 84.37%)"
              isPositive={true}
            />
            <StatCard
              title="Total Customers"
              value={isLoadingMetrics ? '...' : metrics.totalCustomers.toLocaleString()}
              change=""
              timePeriod=""
              icon={<Users />}
              backgroundColor="#FFE2E5"
              iconGradient="linear-gradient(140.67deg, #5E72E4 15.47%, #825EE4 84.37%)"
              isPositive={true}
            />
            <StatCard
              title="Total Reviews Sent"
              value={isLoadingMetrics ? '...' : metrics.totalReviewsSent.toLocaleString()}
              change=""
              timePeriod=""
              icon={<Send />}
              backgroundColor="#FFF4DE"
              iconGradient="linear-gradient(140.67deg, #F5583D 15.47%, #F54351 84.37%)"
              isPositive={true}
            />
            <StatCard
              title="Reviews Posted"
              value={isLoadingMetrics ? '...' : metrics.reviewsPosted.toLocaleString()}
              change=""
              timePeriod=""
              icon={<ClipboardList />}
              backgroundColor="#DCFCE7"
              iconGradient="linear-gradient(140.67deg, #2DCEBC 15.47%, #2DCE96 84.37%)"
              isPositive={true}
            />
            <StatCard
              title="Rewards Claimed"
              value={isLoadingMetrics ? '...' : metrics.rewardsClaimed.toLocaleString()}
              change=""
              timePeriod=""
              icon={<Gift />}
              backgroundColor="#F3E8FF"
              iconGradient="linear-gradient(140.67deg, #FB9C40 15.47%, #FB7B40 84.37%)"
              isPositive={true}
            />
          </Box>

          {/* Review Analytics Chart with Recent Reward Requests */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 3,
              mb: 3
            }}
          >
            <ReviewTrendsChart 
              data={mockQuery.reviewTrends} 
              hasReviews={metrics.reviewsPosted > 0}
            />
            
            {/* Recent Reward Requests */}
            <Card
              sx={{
                border: '1px solid #EAECEF',
                borderRadius: 3,
                boxShadow: 'none'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    mb: 2,
                    color: '#111827'
                  }}
                >
                  Recent Reward Requests
                </Typography>
                {isLoadingRewards ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontFamily: '"Inter", sans-serif',
                      textAlign: 'center',
                      py: 2
                    }}
                  >
                    Loading...
                  </Typography>
                ) : rewardRequests.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontFamily: '"Inter", sans-serif',
                      textAlign: 'center',
                      py: 2
                    }}
                  >
                    No pending reward requests
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {rewardRequests.map((request) => (
                      <RewardRequestItem
                        key={request.id}
                        request={request}
                        onApprove={handleApproveReward}
                        onReject={handleRejectReward}
                      />
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Bottom Row - Reviews by Month, Sentiment Analysis, and Quick Actions */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3
            }}
          >
            <ReviewsByMonthChart 
              data={isLoadingReviewsByMonth ? mockQuery.reviewsByMonth : reviewsByMonth}
              hasReviews={hasMonthlyReviews}
            />
            <SentimentAnalysisChart 
              data={mockQuery.sentimentAnalysis} 
              hasReviews={metrics.reviewsPosted > 0}
            />
            
            {/* Quick Actions */}
            <Card
              sx={{
                border: '1px solid #EAECEF',
                borderRadius: 3,
                boxShadow: 'none'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    mb: 2,
                    color: '#111827'
                  }}
                >
                  Quick Actions
                </Typography>
                <Stack spacing={1.5}>
                  <QuickActionButton
                    icon={<PlusCircle size={20} />}
                    label="Create New Campaign"
                    onClick={() => navigate('/campaigns/create')}
                  />
                  <QuickActionButton
                    icon={<Users size={20} />}
                    label="Manage Customers"
                    onClick={() => navigate('/customers')}
                  />
                  <QuickActionButton
                    icon={<FileText size={20} />}
                    label="View All Reviews"
                    onClick={() => navigate('/reviews')}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;